<template>
  <lf-modal
    v-if="showDealList"
    small
    :title="t('COMMON.DEALS')"
    :close="() => (showDealList = false)"
  >
    <template v-slot:content>
      <deals-filter-dropdown
        :app-by-id="applicationById"
        :app-name-map="applicationsIdToLegalNameMap"
        :app-ids-value="appIdsValue"
        filter-icon
        :toggle-from-parent="showDealList"
        :lazy-load="false"
        :input-info="{
          type: 'radio',
          class: '',
        }"
        @options:update="updateApplicationOptions"
        @value:update="assignSelectedOption"
      >
        <template #header>
          <div v-if="name" class="flex space-x-1 pt-4 pl-3">
            <lf-h4>{{ t("EMAIL_INTEGRATION.ASSIGNED_DEAL") }}:</lf-h4>
            <span class="text-label">{{ name }}</span>
          </div>
        </template>
        <template
          v-slot:options-list="{
            filteredList,
            invertedOptions,
            inputInfo,
            appById,
          }"
        >
          <ul
            v-if="filteredList.length"
            class="max-h-50 overflow-x-clip overflow-y-auto"
          >
            <li
              v-for="option in filteredList"
              :key="option"
              class="p-3"
              :class="[
                {
                  'bg-gray-100': appIdsValueProxy.includes(
                    invertedOptions[option]
                  ),
                },
                applicationId === invertedOptions[option]
                  ? 'opacity-70 grayscale filter'
                  : 'hover:bg-gray-200',
              ]"
            >
              <label
                class="flex items-center space-x-4"
                :class="
                  applicationId === invertedOptions[option]
                    ? 'cursor-not-allowed'
                    : 'cursor-pointer'
                "
              >
                <input
                  v-model="appIdsValueProxy"
                  :type="inputInfo.type"
                  name="quick-filters-applications"
                  :disabled="applicationId === invertedOptions[option]"
                  :class="inputInfo.class"
                  :value="invertedOptions[option]"
                />
                <filter-by-deals-option
                  :deal="appById[invertedOptions[option]]"
                  :is-selected="
                    appIdsValueProxy.includes(invertedOptions[option])
                  "
                />
              </label>
            </li>
          </ul>
          <p v-else class="mb-2 text-center">
            {{ $t("COMMON.NO_DATA_AVAILABLE") }}
          </p>
        </template>

        <template #footer>
          <modal-buttons
            class="justify-end border-t border-gray-200 bg-white py-5"
            :on-cancel="() => (showDealList = false)"
            :on-save="updateDealAssignment"
            :save-button-text="assignText"
            :disabled="loading || !selectedOption"
          />
        </template>
      </deals-filter-dropdown>
    </template>
  </lf-modal>
  <div
    class="flex cursor-pointer items-center space-x-2"
    :class="{ 'text-primary': !isEmailTab }"
    @click.stop="showDealList = !showDealList"
  >
    <icon-base
      v-tooltip="{
        content: assignText,
        onShow: () => isEmailTab,
      }"
      :icon="isEmailTab ? IconEdit : IconAssign"
      data-cy="assign-to-deal-button"
      :class="{ 'scale-75 transform': isEmailTab }"
      class="outline-hidden"
      view-box="0 0 17 17"
    />
    <span v-if="!isEmailTab">{{ assignText }}</span>
  </div>
</template>
<script setup lang="ts">
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";

import { CommunicationType } from "@/enums/communicationLogs";
import { EMAIL_UPDATED, SMS_UPDATED } from "@/helpers/constants/events";
import { useEmitter, usePromiseWrapper } from "@/hooks/common";
import { useFilterByDeals } from "@/hooks/deals";
import i18n from "@/i18n";
import communicationService from "@/services/modules/communication";
import emailService from "@/services/modules/emails";

import DealsFilterDropdown from "@/components/communicationLogs/filters/DealsFilterDropdown.vue";
import FilterByDealsOption from "@/components/communicationLogs/filters/FilterByDealsOption.vue";
import IconAssign from "@/components/icons/IconAssign.vue";
import IconEdit from "@/components/icons/IconEdit.vue";
import IconBase from "@/components/ui/IconBase.vue";
import LfModal from "@/components/ui/Modal.vue";
import ModalButtons from "@/components/ui/ModalButtons.vue";
import LfH4 from "@/components/ui/text/LfHeading4.vue";

import type { Nullish } from "@/models/common";

interface Props {
  id: number;
  applicationId: string | Nullish;
  name: string | Nullish;
  isEmailTab?: boolean;
  assignText?: string;
  type: CommunicationType.email | CommunicationType.sms;
}

const {
  id,
  applicationId,
  name,
  isEmailTab = false,
  assignText = i18n.global.t("EMAIL_INTEGRATION.ASSIGN_TO_DEAL"),
  type = CommunicationType.email,
} = defineProps<Props>();

const {
  applicationById,
  applicationsIdToLegalNameMap,
  updateApplicationOptions,
} = useFilterByDeals();

const { t } = useI18n();

const emitter = useEmitter();

const showDealList = ref(false);
const appIdsValue = ref(null);
const selectedOption = ref("");

const appIdsValueProxy = computed({
  get() {
    if (appIdsValue.value === null) {
      return [];
    }
    return appIdsValue.value as string[];
  },
  set(newAppIdsValue) {
    assignSelectedOption(newAppIdsValue);
  },
});

const assignSelectedOption = (value: string[] | string) => {
  selectedOption.value = Array.isArray(value) ? value[0] : value;
};

const { fetchWrapper: updateDealAssignment, loading } = usePromiseWrapper(
  async () => {
    if (!selectedOption.value) {
      return;
    }
    const updateMethod =
      type === CommunicationType.email
        ? emailService.assignEmailToApplication
        : communicationService.assignSmsToApplication;
    await updateMethod(selectedOption.value, id);
    showDealList.value = false;
    emitter.emit(
      type === CommunicationType.email ? EMAIL_UPDATED : SMS_UPDATED
    );
    selectedOption.value = "";
  }
);
</script>
<style scoped>
@reference "@/assets/tailwind.css";

.assign-email-to-deal :deep(.deals-filter) {
  @apply w-80;
  .legal-name {
    @apply w-25;
  }
}
</style>
